import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  IconEye,
  IconCode,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconGripVertical,
} from '@tabler/icons-react';
import { useCardBuilder } from '../../hooks/useCardBuilder';
import type { CardSurvey, SurveyQuestionType, SurveyPath, CardQuestion } from '../../types';
import { QuestionBlock } from './QuestionBlock';
import { QuestionTypeBar } from './QuestionTypeBar';
import { PathSection } from './PathSection';
import { PathWizard } from '../PathWizard/PathWizard';
import { SurveyValidationAlert } from './SurveyValidationAlert';
import { validateSurvey } from '../../utils/surveyValidation';
import { normalizeTriggerValues } from '../../utils/pathTriggerValues';

interface CardSurveyBuilderProps {
  initialSurvey?: CardSurvey;
  onSurveyChange?: (survey: CardSurvey) => void;
  onPreview?: () => void;
}

export function CardSurveyBuilder({
  initialSurvey,
  onSurveyChange,
  onPreview,
}: CardSurveyBuilderProps) {
  const {
    questions,
    paths,
    addQuestion,
    addQuestionToPath,
    duplicateQuestion,
    updateQuestion,
    deleteQuestion,
    reorderMainQuestionBlock,
    moveMainQuestionBlockToIndex,
    moveQuestionToPath,
    moveQuestionOutOfPath,
    reorderQuestionInPath,
    moveQuestionWithinPath,
    moveQuestionToPathAt,
    addPath,
    updatePath,
    deletePath,
    getCardSurvey,
    getQuestionById,
    getPathById,
    getQuestionsByPath,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useCardBuilder(initialSurvey);

  const [pathWizardOpen, setPathWizardOpen] = useState(false);
  const [editingPathQuestionId, setEditingPathQuestionId] = useState<string | null>(null);
  const [editingPath, setEditingPath] = useState<SurveyPath | null>(null);
  const [initialTriggerValues, setInitialTriggerValues] = useState<string[]>([]);
  const [expandedAccordion, setExpandedAccordion] = useState<string[]>(['panel-questions']);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [draggingBlockId, setDraggingBlockId] = useState<string | null>(null);
  const [draggingPathQuestionId, setDraggingPathQuestionId] = useState<string | null>(null);
  const blockRefs = useRef<Record<string, HTMLElement | null>>({});

  const handleBlockDragEnd = useCallback(() => {
    setDraggingBlockId(null);
    setDraggingPathQuestionId(null);
    setDropTargetIndex(null);
  }, []);

  const handleQuestionDragStart = useCallback((e: React.DragEvent, questionId: string) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'question', questionId }));
    e.dataTransfer.effectAllowed = 'move';
    const q = getQuestionById(questionId);
    if (q?.pathId) setDraggingPathQuestionId(questionId);
  }, [getQuestionById]);

  const handleBlockDrop = useCallback(
    (e: React.DragEvent, targetMainIndex: number) => {
      e.preventDefault();
      setDropTargetIndex(null);
      setDraggingBlockId(null);
      setDraggingPathQuestionId(null);
      try {
        const data = e.dataTransfer.getData('application/json');
        const payload = data ? JSON.parse(data) : null;
        if (payload?.type === 'main-block' && payload?.mainQuestionId) {
          moveMainQuestionBlockToIndex(payload.mainQuestionId, targetMainIndex);
        } else if (payload?.type === 'question' && payload?.questionId) {
          const q = getQuestionById(payload.questionId);
          if (q?.pathId) moveQuestionOutOfPath(payload.questionId, targetMainIndex);
        }
      } catch (err) {
        if (import.meta.env?.DEV) {
          console.warn('CardSurveyBuilder: invalid drop data (JSON parse failed)', err);
        }
      }
    },
    [moveMainQuestionBlockToIndex, moveQuestionOutOfPath, getQuestionById]
  );

  const isDraggingAny = draggingBlockId != null || draggingPathQuestionId != null;

  const handleBlockDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (isDraggingAny) setDropTargetIndex(index);
  }, [isDraggingAny]);

  const handleBlockDragOverBlock = useCallback(
    (e: React.DragEvent, displayIndex: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!isDraggingAny) return;
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      setDropTargetIndex(e.clientY < mid ? displayIndex : displayIndex + 1);
    },
    [isDraggingAny]
  );

  const handleBlockDragLeave = useCallback(() => {
    setDropTargetIndex(null);
  }, []);

  React.useLayoutEffect(() => {
    if (onSurveyChange) {
      onSurveyChange(getCardSurvey());
    }
  }, [questions, paths, onSurveyChange, getCardSurvey]);

  const handleAddQuestion = (type: SurveyQuestionType) => {
    addQuestion(type);
  };

  const handleCreatePath = (questionId?: string, optionValue?: string) => {
    if (questionId) {
      setEditingPathQuestionId(questionId);
    }
    setEditingPath(null);
    setInitialTriggerValues(optionValue != null ? [optionValue] : []);
    setPathWizardOpen(true);
  };

  const handleEditPath = (questionId: string) => {
    const question = getQuestionById(questionId);
    if (question?.pathId) {
      const path = getPathById(question.pathId);
      if (path) {
        setEditingPath(path);
        setEditingPathQuestionId(questionId);
        setPathWizardOpen(true);
      }
    }
  };

  const handleEditPathForOption = (questionId: string, optionValue: string) => {
    const pathForOption = paths.find(
      (p) => p.trigger.questionId === questionId && p.trigger.values.includes(optionValue)
    );
    if (pathForOption) {
      setEditingPath(pathForOption);
      setEditingPathQuestionId(questionId);
      setInitialTriggerValues([]);
      setPathWizardOpen(true);
    }
  };

  const handleSavePath = (pathData: Omit<SurveyPath, 'id'>) => {
    const triggerValuesArr = normalizeTriggerValues(pathData.trigger.values);
    const normalizedPathData: Omit<SurveyPath, 'id'> = {
      ...pathData,
      trigger: { ...pathData.trigger, values: triggerValuesArr },
    };
    if (editingPath) {
      updatePath(editingPath.id, normalizedPathData);
      normalizedPathData.questionIds.forEach((qId) => {
        updateQuestion(qId, { pathId: editingPath.id });
      });
    } else {
      const pathId = addPath(normalizedPathData);
      normalizedPathData.questionIds.forEach((qId) => {
        updateQuestion(qId, { pathId });
      });
    }
    setPathWizardOpen(false);
    setEditingPath(null);
    setEditingPathQuestionId(null);
    setInitialTriggerValues([]);
  };

  const mainQuestions = questions.filter((q) => !q.pathId);

  const displayMainQuestions = useMemo(() => {
    if (!draggingBlockId || dropTargetIndex == null) return mainQuestions;
    const fromIndex = mainQuestions.findIndex((q) => q.id === draggingBlockId);
    if (fromIndex === -1) return mainQuestions;
    const without = mainQuestions.filter((q) => q.id !== draggingBlockId);
    const toIndex = Math.min(Math.max(0, dropTargetIndex), without.length);
    const dragged = mainQuestions[fromIndex];
    return [...without.slice(0, toIndex), dragged, ...without.slice(toIndex)];
  }, [mainQuestions, draggingBlockId, dropTargetIndex]);

  const handleBlockDragStart = useCallback(
    (e: React.DragEvent, mainQuestionId: string) => {
      e.dataTransfer.setData('application/json', JSON.stringify({ type: 'main-block', mainQuestionId }));
      e.dataTransfer.effectAllowed = 'move';
      setDraggingBlockId(mainQuestionId);
      const currentIndex = mainQuestions.findIndex((q) => q.id === mainQuestionId);
      setDropTargetIndex(currentIndex >= 0 ? currentIndex : null);
      const el = blockRefs.current[mainQuestionId];
      if (el) {
        const rect = el.getBoundingClientRect();
        e.dataTransfer.setDragImage(el, Math.min(rect.width / 2, 120), 24);
      }
    },
    [mainQuestions]
  );

  const pathQuestionsMap = new Map<string, CardQuestion[]>();
  paths.forEach((path) => {
    const pathQuestions = getQuestionsByPath(path.id);
    pathQuestionsMap.set(path.id, pathQuestions);
  });

  const pathsByTrigger = new Map<string, SurveyPath[]>();
  paths.forEach((path) => {
    const triggerId = path.trigger.questionId;
    if (!pathsByTrigger.has(triggerId)) {
      pathsByTrigger.set(triggerId, []);
    }
    pathsByTrigger.get(triggerId)!.push(path);
  });

  const handlePathDelete = (pathId: string) => {
    const path = getPathById(pathId);
    if (path) {
      path.questionIds.forEach((qId) => {
        updateQuestion(qId, { pathId: undefined });
      });
    }
    deletePath(pathId);
  };

  const handleRemovePathFromOption = (questionId: string, optionValue: string) => {
    const path = paths.find(
      (p) => p.trigger.questionId === questionId && normalizeTriggerValues(p.trigger.values).includes(optionValue)
    );
    if (!path) return;
    const newValues = normalizeTriggerValues(path.trigger.values).filter((v) => v !== optionValue);
    if (newValues.length === 0) {
      handlePathDelete(path.id);
    } else {
      updatePath(path.id, { trigger: { ...path.trigger, values: newValues } });
    }
  };

  const handleOptionRemove = (questionId: string, optionValue: string) => {
    const path = paths.find(
      (p) => p.trigger.questionId === questionId && normalizeTriggerValues(p.trigger.values).includes(optionValue)
    );
    if (path) {
      const newValues = normalizeTriggerValues(path.trigger.values).filter((v) => v !== optionValue);
      if (newValues.length === 0) {
        handlePathDelete(path.id);
      } else {
        updatePath(path.id, { trigger: { ...path.trigger, values: newValues } });
      }
    }
    const q = getQuestionById(questionId);
    if (q?.config?.options) {
      updateQuestion(questionId, {
        config: { ...q.config, options: q.config.options.filter((o) => o !== optionValue) },
      });
    }
  };

  const handlePathEdit = (path: SurveyPath, triggerQuestion: CardQuestion) => {
    const pQuestions = pathQuestionsMap.get(path.id) || [];
    const firstPathQuestion = pQuestions[0];
    
    if (firstPathQuestion) {
      handleEditPath(firstPathQuestion.id);
    } else {
      setEditingPathQuestionId(triggerQuestion.id);
      setEditingPath(path);
      setPathWizardOpen(true);
    }
  };

  const jsonPreview = useMemo(() => {
    const survey = getCardSurvey();
    return JSON.stringify(survey, null, 2);
  }, [getCardSurvey]);

  const validationIssues = useMemo(() => {
    const survey = getCardSurvey();
    return validateSurvey(survey);
  }, [getCardSurvey]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (pathWizardOpen) {
        if (e.key === 'Escape') {
          setPathWizardOpen(false);
          setEditingPath(null);
          setEditingPathQuestionId(null);
        }
        return;
      }
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      if (mod && e.key === 'y') {
        e.preventDefault();
        redo();
      }
      if (mod && e.key === 'p') {
        e.preventDefault();
        onPreview?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pathWizardOpen, undo, redo, onPreview]);

  const handleFocusQuestion = useCallback((questionId: string) => {
    setExpandedAccordion(['panel-questions']);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-question-id="${questionId}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });
  }, []);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-1">
        <h5 className="text-xl font-semibold">Survey Builder</h5>
        <div className="flex items-center gap-0.5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button variant="ghost" size="icon" onClick={undo} disabled={!canUndo} />
                }
              >
                <IconArrowBackUp className="size-5" />
              </TooltipTrigger>
              <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button variant="ghost" size="icon" onClick={redo} disabled={!canRedo} />
                }
              >
                <IconArrowForwardUp className="size-5" />
              </TooltipTrigger>
              <TooltipContent>Redo (Ctrl+Shift+Z)</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {onPreview && (
            <Button variant="outline" onClick={onPreview}>
              <IconEye className="size-4" />
              Preview
            </Button>
          )}
        </div>
      </div>

      {validationIssues.length > 0 && (
        <SurveyValidationAlert
          issues={validationIssues}
          onFocusQuestion={handleFocusQuestion}
        />
      )}

      <Accordion
        value={expandedAccordion}
        onValueChange={setExpandedAccordion}
        className="mb-2"
      >
        <AccordionItem value="panel-questions" className="rounded-lg border shadow-sm">
          <AccordionTrigger className="px-3 hover:no-underline">
            <span className="text-lg font-semibold">
              Questions & Paths ({mainQuestions.length} main questions, {paths.length} paths)
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            {mainQuestions.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No questions yet. Add a question to get started.
              </p>
            ) : (
              <>
                {displayMainQuestions.map((question, displayIndex) => {
                  const triggerPaths = pathsByTrigger.get(question.id) || [];
                  const isDragging = draggingBlockId === question.id;

                  return (
                    <React.Fragment key={question.id}>
                      <div
                        onDrop={(e) => handleBlockDrop(e, displayIndex)}
                        onDragOver={(e) => handleBlockDragOver(e, displayIndex)}
                        onDragLeave={handleBlockDragLeave}
                        className="rounded transition-all duration-150"
                        style={{
                          minHeight: 10,
                          height: dropTargetIndex === displayIndex ? 16 : 10,
                          marginLeft: -4,
                          marginRight: -4,
                          backgroundColor: dropTargetIndex === displayIndex ? 'hsl(var(--primary))' : 'transparent',
                          opacity: dropTargetIndex === displayIndex ? 0.35 : 0,
                        }}
                      />

                      <div
                        ref={(el: HTMLDivElement | null) => {
                          blockRefs.current[question.id] = el;
                        }}
                        onDragOver={(e) => handleBlockDragOverBlock(e, displayIndex)}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleBlockDrop(e, dropTargetIndex ?? displayIndex);
                        }}
                        className={cn(
                          'flex items-start gap-0.5 transition-opacity duration-150',
                          isDragging && 'opacity-50'
                        )}
                      >
                        {question.type !== 'done' && (
                          <div
                            draggable
                            onDragStart={(e) => handleBlockDragStart(e, question.id)}
                            onDragEnd={handleBlockDragEnd}
                            className={cn(
                              'flex-shrink-0 pt-3 text-muted-foreground',
                              isDragging ? 'cursor-grabbing' : 'cursor-grab'
                            )}
                            title="Drag to reorder (moves this question and its branches)"
                          >
                            <IconGripVertical className="size-4" />
                          </div>
                        )}

                        <div className="min-w-0 flex-1">
                          <QuestionBlock
                            question={question}
                            path={undefined}
                            triggerPaths={triggerPaths}
                            onUpdate={(updates) => updateQuestion(question.id, updates)}
                            onDelete={() => deleteQuestion(question.id)}
                            onDuplicate={() => duplicateQuestion(question.id)}
                            onMoveUp={
                              question.type === 'done'
                                ? undefined
                                : displayIndex > 0
                                  ? () => reorderMainQuestionBlock(question.id, 'up')
                                  : undefined
                            }
                            onMoveDown={
                              question.type === 'done'
                                ? undefined
                                : displayIndex < displayMainQuestions.length - 1
                                  ? () => reorderMainQuestionBlock(question.id, 'down')
                                  : undefined
                            }
                            onCreatePath={(optionValue) => handleCreatePath(question.id, optionValue)}
                            onEditPathForOption={(optionValue) => handleEditPathForOption(question.id, optionValue)}
                            onRemovePathFromOption={(optionValue) => handleRemovePathFromOption(question.id, optionValue)}
                            onOptionRemove={(optionValue) => handleOptionRemove(question.id, optionValue)}
                            onEditPath={undefined}
                            canMoveUp={question.type === 'done' ? false : displayIndex > 0}
                            canMoveDown={question.type === 'done' ? false : displayIndex < displayMainQuestions.length - 1}
                            onDragStartForPath={question.type === 'done' ? undefined : (e) => handleQuestionDragStart(e, question.id)}
                          />

                          {triggerPaths.map((path) => {
                            const pQuestions = pathQuestionsMap.get(path.id) || [];
                            return (
                              <PathSection
                                key={path.id}
                                path={path}
                                pathQuestions={pQuestions}
                                triggerQuestion={question}
                                allQuestions={questions}
                                onQuestionUpdate={(qId, updates) => updateQuestion(qId, updates)}
                                onQuestionDelete={(qId) => deleteQuestion(qId)}
                                onQuestionDuplicate={(qId: string) => duplicateQuestion(qId)}
                                onPathEdit={() => handlePathEdit(path, question)}
                                onPathDelete={() => handlePathDelete(path.id)}
                                onAddQuestionToPath={(type) => addQuestionToPath(type, path.id)}
                                onDropQuestion={(qId) => moveQuestionToPath(qId, path.id)}
                                onDropQuestionAtIndex={(qId, targetPathIndex) => {
                                  const q = getQuestionById(qId);
                                  if (q?.pathId === path.id) {
                                    moveQuestionWithinPath(qId, targetPathIndex);
                                  } else {
                                    moveQuestionToPathAt(qId, path.id, targetPathIndex);
                                  }
                                }}
                                onQuestionDragStart={handleQuestionDragStart}
                                onQuestionDragEnd={handleBlockDragEnd}
                                onReorderInPath={reorderQuestionInPath}
                                defaultExpanded={true}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}

                <div
                  onDrop={(e) => handleBlockDrop(e, displayMainQuestions.length)}
                  onDragOver={(e) => handleBlockDragOver(e, displayMainQuestions.length)}
                  onDragLeave={handleBlockDragLeave}
                  className="rounded transition-all duration-150"
                  style={{
                    minHeight: 10,
                    height: dropTargetIndex === displayMainQuestions.length ? 16 : 10,
                    marginLeft: -4,
                    marginRight: -4,
                    backgroundColor: dropTargetIndex === displayMainQuestions.length ? 'hsl(var(--primary))' : 'transparent',
                    opacity: dropTargetIndex === displayMainQuestions.length ? 0.35 : 0,
                  }}
                />
              </>
            )}

            <QuestionTypeBar onAddQuestion={handleAddQuestion} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="panel-json" className="mt-2 rounded-lg border shadow-sm">
          <AccordionTrigger className="px-3 hover:no-underline">
            <div className="flex items-center gap-1">
              <IconCode className="size-5" />
              <span className="text-lg font-semibold">JSON Preview</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <p className="mb-2 text-sm text-muted-foreground">
              This is the complete survey structure in JSON format. This data is automatically generated from your questions and paths.
            </p>
            <div className="max-h-[600px] overflow-auto rounded border bg-muted/50 p-2">
              <pre className="m-0 whitespace-pre-wrap break-words font-mono text-sm">
                {jsonPreview}
              </pre>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <PathWizard
        open={pathWizardOpen}
        onClose={() => {
          setPathWizardOpen(false);
          setEditingPath(null);
          setEditingPathQuestionId(null);
          setInitialTriggerValues([]);
        }}
        onSave={handleSavePath}
        questions={questions}
        existingPaths={paths}
        existingPath={editingPath || undefined}
        triggerQuestionId={editingPathQuestionId || undefined}
        initialTriggerValues={initialTriggerValues}
      />
    </div>
  );
}
